import { useRef, useState } from "react";
import { Camera, Trash2, Loader2, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import html2canvas from "html2canvas";

interface ScreenshotCaptureProps {
  onScreenshotCapture: (blob: Blob | null) => void;
  screenshotBlob: Blob | null;
}

export function ScreenshotCapture({ onScreenshotCapture, screenshotBlob }: ScreenshotCaptureProps) {
  const [isCapturing, setIsCapturing] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const setBlob = (blob: Blob | null) => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
    onScreenshotCapture(blob);
    if (blob) setPreviewUrl(URL.createObjectURL(blob));
  };

  const captureScreenshot = async () => {
    setIsCapturing(true);

    // Hide every element the feedback UI owns (panel, launcher, overlays)
    // so the capture only contains the underlying page.
    const hidden = Array.from(
      document.querySelectorAll<HTMLElement>("[data-feedback-ui]"),
    );
    const prev = hidden.map((el) => el.style.visibility);
    hidden.forEach((el) => (el.style.visibility = "hidden"));

    try {
      // Let the browser paint the hidden state.
      await new Promise((r) => setTimeout(r, 120));

      const canvas = await html2canvas(document.body, {
        useCORS: true,
        allowTaint: true,
        scale: 1,
        ignoreElements: (el) =>
          el instanceof HTMLElement && el.dataset.feedbackUi !== undefined,
      });

      await new Promise<void>((resolve) => {
        canvas.toBlob((blob) => {
          if (blob) setBlob(blob);
          resolve();
        }, "image/png");
      });
    } catch (error) {
      console.error("Error capturing screenshot:", error);
    } finally {
      hidden.forEach((el, i) => (el.style.visibility = prev[i] ?? ""));
      setIsCapturing(false);
    }
  };

  const onFilePicked = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && file.type.startsWith("image/")) {
      setBlob(file);
    }
    // Allow re-picking the same file.
    e.target.value = "";
  };

  return (
    <div className="space-y-2">
      <label className="text-sm font-medium">Screenshot (optional)</label>

      {!screenshotBlob ? (
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={captureScreenshot}
            disabled={isCapturing}
          >
            {isCapturing ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Camera className="h-4 w-4 mr-2" />
            )}
            {isCapturing ? "Capturing..." : "Capture screen"}
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            disabled={isCapturing}
          >
            <Upload className="h-4 w-4 mr-2" />
            Attach image
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={onFilePicked}
          />
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <img
            src={previewUrl || undefined}
            alt="Attached screenshot"
            className="h-16 w-24 object-cover rounded-md border"
          />
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setBlob(null)}
          >
            <Trash2 className="h-4 w-4 mr-1" />
            Remove
          </Button>
        </div>
      )}
    </div>
  );
}
